import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Package,
  Camera,
  Sparkle,
  Trash,
  FileXls,
  FilePdf,
  Plus,
  ArrowsClockwise,
  Minus,
  X,
  Upload,
  PencilSimple,
  ArrowClockwise,
  Check,
  Barcode,
  CaretUp,
  CaretDown,
  CaretUpDown,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { openDownload } from "@/lib/downloadUrl";
import { FotoCameraModal } from "@/components/FotoCameraModal";
import { verkleinFoto } from "@/lib/compressImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtEUR = (v) =>
  v === null || v === undefined || v === ""
    ? "—"
    : Number(v).toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-BE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// Enkel het kleurdeel: "26WBAG002-CAMEL" → "CAMEL"
const kleurDeel = (kleurcode, barcode) => {
  const code = (kleurcode || "").trim();
  const bc = (barcode || "").trim();
  if (bc && code.toUpperCase().startsWith(bc.toUpperCase())) {
    return code.slice(bc.length).replace(/^[\s\-_/]+/, "") || code;
  }
  if (code.includes("-")) return code.split("-").slice(1).join("-").trim() || code;
  return code;
};

const fmtTijd = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const emptyItem = () => ({
  kleurcode: "",
  barcode: "",
  omschrijving: "",
  merk: "",
  maat: "",
  prijs: "",
  aantal: 1,
  per_pack: 1,
  extra: {},
  foto_url: null,
});

// ── Beep (Web Audio API) ────────────────────────────────
let _audioCtx = null;

// Dedup identieke etiketten (zelfde kleurcode + barcode) → aantal ophogen
function dedupLabels(labels) {
  const map = new Map();
  for (const l of labels) {
    const key = `${(l.kleurcode || "").trim().toLowerCase()}|${(l.barcode || "").trim()}`;
    const incoming = Math.max(1, parseInt(l.aantal || 1, 10));
    if (map.has(key)) {
      const existing = map.get(key);
      existing.aantal = (existing.aantal || 1) + incoming;
      for (const k of ["omschrijving", "merk", "maat", "prijs", "foto_url"]) {
        if (!existing[k] && l[k]) existing[k] = l[k];
      }
    } else {
      map.set(key, { ...l, aantal: incoming, _selected: true });
    }
  }
  return Array.from(map.values());
}

// Klikbare kolomkop om te sorteren
function SortTh({ k, sort, onClick, align = "left", children }) {
  const actief = sort.key === k;
  const just = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
  const txt = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  return (
    <th className={`p-2 ${txt}`}>
      <button
        type="button"
        onClick={() => onClick(k)}
        className={`flex items-center gap-1 w-full ${just} uppercase tracking-wider font-bold hover:text-[#002FA7] transition-colors ${actief ? "text-[#002FA7]" : ""}`}
        data-testid={`sort-${k}`}
      >
        {children}
        {actief ? (
          sort.dir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />
        ) : (
          <CaretUpDown size={11} weight="bold" className="opacity-30" />
        )}
      </button>
    </th>
  );
}

// ── SwipeableRow: iOS-stijl swipe-links om te verwijderen ─
// Vind de dichtsbijzijnde <tr> en apply transform + kleur-hint.
function SwipeableRow({ item, onDelete, children, isNieuw = false, "data-testid": testId }) {
  const rowRef = useRef(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const swipingRef = useRef(false);
  const [dx, setDx] = useState(0);
  const [threshold] = useState(120); // px

  const onTouchStart = (e) => {
    const t = e.touches[0];
    startXRef.current = t.clientX;
    startYRef.current = t.clientY;
    swipingRef.current = true;
  };
  const onTouchMove = (e) => {
    if (!swipingRef.current) return;
    const t = e.touches[0];
    const dxRaw = t.clientX - startXRef.current;
    const dyRaw = t.clientY - startYRef.current;
    // Alleen horizontaal (vermijd conflict met scroll)
    if (Math.abs(dyRaw) > Math.abs(dxRaw)) return;
    if (dxRaw < 0) {
      setDx(Math.max(-220, dxRaw));
      e.preventDefault();
    } else {
      setDx(0);
    }
  };
  const onTouchEnd = () => {
    swipingRef.current = false;
    if (dx < -threshold) {
      // Trigger delete met visuele fade-uit
      setDx(-window.innerWidth);
      setTimeout(() => onDelete(item), 180);
    } else {
      setDx(0);
    }
  };

  const bgOpacity = Math.min(1, Math.abs(dx) / threshold);
  return (
    <tr
      ref={rowRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      className={`border-t border-border hover:bg-muted/30 select-none ${isNieuw ? "bg-emerald-50" : ""}`}
      data-testid={testId}
      style={{
        transform: dx !== 0 ? `translateX(${dx}px)` : undefined,
        transition: swipingRef.current ? "none" : "transform 0.2s ease-out",
        backgroundColor: dx < 0 ? `rgba(220, 38, 38, ${bgOpacity * 0.15})` : undefined,
        position: "relative",
      }}
    >
      {/* Delete indicator die verschijnt door de swipe */}
      {dx < -10 && (
        <td
          style={{
            position: "absolute",
            right: `${dx}px`,
            top: 0,
            bottom: 0,
            width: Math.min(Math.abs(dx), 220),
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            paddingRight: 16,
            background: "#dc2626",
            color: "white",
            fontWeight: "bold",
            fontSize: "13px",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <Trash size={18} weight="bold" style={{ marginRight: 6 }} />
          {Math.abs(dx) >= threshold ? "LOSLATEN" : "Wissen"}
        </td>
      )}
      {children}
    </tr>
  );
}function playBeep({ frequency = 880, duration = 120, volume = 0.25 } = {}) {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    if (ctx.state === "suspended") ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = frequency;
    gain.gain.value = volume;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration / 1000);
    osc.stop(ctx.currentTime + duration / 1000);
  } catch {
    /* ignore */
  }
}

// ── Barcode Camera Scanner Modal (Native BarcodeDetector + ZXing fallback) ─
// Continu-modus: blijft open, piept bij elke scan, herbewapent na 1.2s
function BarcodeScannerModal({ open, onClose, onScan }) {
  const videoRef = useRef(null);
  const stopFnRef = useRef(null);
  const armedRef = useRef(true);
  const [error, setError] = useState("");
  const [engine, setEngine] = useState("");
  const [lastScan, setLastScan] = useState("");
  const [scanCount, setScanCount] = useState(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    setEngine("");
    setLastScan("");
    setScanCount(0);
    armedRef.current = true;

    const handleResult = (text) => {
      if (cancelled || !text || !armedRef.current) return;
      const clean = String(text).trim();
      armedRef.current = false;
      playBeep();
      setLastScan(clean);
      setScanCount((c) => c + 1);
      Promise.resolve(onScan(clean)).catch(() => {});
      setTimeout(() => {
        if (!cancelled) armedRef.current = true;
      }, 1200);
    };

    const cleanup = () => {
      try { stopFnRef.current?.(); } catch { /* ignore */ }
      stopFnRef.current = null;
      if (videoRef.current?.srcObject) {
        try { videoRef.current.srcObject.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
        videoRef.current.srcObject = null;
      }
    };

    (async () => {
      try {
        const hasNative = "BarcodeDetector" in window;
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        if (!videoRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute("playsinline", "true");
        videoRef.current.muted = true;
        await videoRef.current.play().catch(() => {});

        if (hasNative) {
          try {
            const Detector = window.BarcodeDetector;
            const supported = (await Detector.getSupportedFormats?.()) || [];
            const preferred = ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "code_93", "codabar", "itf", "qr_code", "data_matrix", "pdf417", "aztec"];
            const formats = preferred.filter((f) => supported.length === 0 ? true : supported.includes(f));
            const detector = new Detector({ formats });
            setEngine("native");
            let running = true;
            stopFnRef.current = () => { running = false; };
            const tick = async () => {
              if (!running || cancelled || !videoRef.current) return;
              try {
                const results = await detector.detect(videoRef.current);
                if (results && results[0]?.rawValue) handleResult(results[0].rawValue);
              } catch { /* per-frame */ }
              if (running && !cancelled) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            return;
          } catch { /* fall through */ }
        }

        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        if (cancelled) return;
        const reader = new BrowserMultiFormatReader();
        setEngine("zxing");
        const controls = await reader.decodeFromVideoElement(videoRef.current, (result) => {
          if (result?.getText) handleResult(result.getText());
        });
        stopFnRef.current = () => { try { controls.stop(); } catch { /* ignore */ } };
      } catch {
        if (!cancelled) setError("Camera niet beschikbaar. Geef toestemming voor camera-toegang.");
      }
    })();

    return () => { cancelled = true; cleanup(); };
  }, [open, onScan]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Barcode size={18} weight="bold" /> Scan barcode {scanCount > 0 && <Badge variant="secondary" className="ml-1">{scanCount}</Badge>}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Blijf richten op nieuwe barcodes — elke geldige scan geeft een piep. Sluit als je klaar bent.
            {engine === "native" && (
              <span className="ml-1 text-[#002FA7] font-semibold">· Native detector actief</span>
            )}
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <div className="text-sm text-[#E53E3E] py-4">{error}</div>
        ) : (
          <div className="relative w-full aspect-[16/10] bg-black rounded-sm overflow-hidden">
            <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" playsInline muted autoPlay />
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-[78%] h-[38%] border-2 border-white/80 rounded-sm shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
            </div>
            {lastScan && (
              <div className="absolute bottom-2 left-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-sm font-mono">
                Laatst: {lastScan}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="scanner-close-btn">
            <Check size={16} weight="bold" className="mr-1" /> Klaar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function StockBijvullen() {
  const [items, setItems] = useState([]);
  const [filialen, setFilialen] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);

  const [filterFiliaal, setFilterFiliaal] = useState("all");
  const [filterDatum, setFilterDatum] = useState("today"); // all|today|week|month
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Scan preview dialog
  const [scanDialog, setScanDialog] = useState({ open: false, labels: [] });

  // Barcode scanner
  const [barcodeScannerOpen, setBarcodeScannerOpen] = useState(false);

  // Live foto camera (geen "gebruik deze foto" bevestiging)
  const [cameraOpen, setCameraOpen] = useState(false);

  // Direct opslaan: herkende etiketten gaan meteen in de lijst
  const [directOpslaan, setDirectOpslaan] = useState(
    () => localStorage.getItem("stock_direct_opslaan") !== "0"
  );
  const [nieuweIds, setNieuweIds] = useState(new Set());
  const directOpslaanRef = useRef(directOpslaan);
  directOpslaanRef.current = directOpslaan;

  // Verzamelmodus: meerdere foto's opsparen en in 1 batch naar AI sturen
  const [pendingPhotos, setPendingPhotos] = useState([]); // [{id, file, url, status, aantalLabels}]

  const scanStats = useMemo(
    () => ({
      bezig: pendingPhotos.filter((p) => p.status === "scanning").length,
      klaar: pendingPhotos.filter((p) => p.status !== "scanning").length,
      labels: pendingPhotos.reduce((s, p) => s + (p.aantalLabels || 0), 0),
    }),
    [pendingPhotos]
  );

  // Manual add / edit dialog
  const [editDialog, setEditDialog] = useState({ open: false, item: null, isNew: true });

  // Filiaal choice for new items
  const [defaultFiliaal, setDefaultFiliaal] = useState("");
  // Klanten (los van filialen, puur voor controle)
  const [klanten, setKlanten] = useState([]);
  const [defaultKlant, setDefaultKlant] = useState("");
  const [nieuweKlant, setNieuweKlant] = useState("");

  const doelWaarde = defaultKlant ? `kl:${defaultKlant}` : (defaultFiliaal ? `fil:${defaultFiliaal}` : "");
  const kiesDoel = (v) => {
    if (v.startsWith("kl:")) { setDefaultKlant(v.slice(3)); setDefaultFiliaal(""); }
    else { setDefaultFiliaal(v.slice(4)); setDefaultKlant(""); }
  };
  const doelPayload = () => ({
    filiaal_id: defaultFiliaal || null,
    klant_id: defaultKlant || null,
  });
  const doelRef = useRef({ filiaal_id: null, klant_id: null });
  doelRef.current = { filiaal_id: defaultFiliaal || null, klant_id: defaultKlant || null };
  const filterParams = () => {
    if (filterFiliaal === "all") return {};
    if (filterFiliaal.startsWith("kl:")) return { klant_id: filterFiliaal.slice(3) };
    return { filiaal_id: filterFiliaal.replace(/^fil:/, "") };
  };

  const fileInputRef = useRef(null);

  // ── Data loaders ─────────────────────────────────────────────
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterFiliaal !== "all") Object.assign(params, filterParams());
      const res = await axios.get(`${API}/stock-bijvullen`, { params });
      setItems(res.data || []);
    } catch {
      toast.error("Fout bij laden stocklijst");
    } finally {
      setLoading(false);
    }
  }, [filterFiliaal]);

  const fetchFilialen = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/filialen`);
      setFilialen(res.data || []);
      if ((res.data || []).length && !defaultFiliaal) {
        setDefaultFiliaal(res.data[0].id);
      }
    } catch {
      /* ignore */
    }
  }, [defaultFiliaal]);

  const fetchKlanten = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stock-bijvullen/klanten`);
      setKlanten(res.data || []);
    } catch {
      /* ignore */
    }
  }, []);

  const voegKlantToe = async () => {
    const naam = nieuweKlant.trim();
    if (!naam) return;
    try {
      const res = await axios.post(`${API}/stock-bijvullen/klanten`, { naam });
      setNieuweKlant("");
      await fetchKlanten();
      setDefaultKlant(res.data.id);
      setDefaultFiliaal("");
      toast.success(`Klant "${naam}" toegevoegd`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toevoegen mislukt");
    }
  };

  const verwijderKlant = async (k) => {
    if (!window.confirm(`Klant "${k.naam}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/stock-bijvullen/klanten/${k.id}`);
      if (defaultKlant === k.id) setDefaultKlant("");
      if (filterFiliaal === `kl:${k.id}`) setFilterFiliaal("all");
      fetchKlanten();
      toast.success("Klant verwijderd");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  useEffect(() => {
    fetchKlanten();
  }, [fetchKlanten]);

  useEffect(() => {
    fetchFilialen();
  }, [fetchFilialen]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // ── Scan flow: directe AI-scan per foto ──────────────────────
  // Elke foto gaat meteen naar de AI terwijl je verder scant.
  // handleScanBatch toont alleen nog de verzamelde resultaten (wacht enkel op lopende scans).
  const scanJobsRef = useRef(new Map()); // id -> Promise
  const scanResultsRef = useRef(new Map()); // id -> labels[]
  const photoIdRef = useRef(0);

  // Slaat herkende etiketten direct op in de lijst en markeert ze als "nieuw"
  const bewaarLabels = useCallback(async (labels) => {
    let ok = 0;
    const ids = [];
    for (const lbl of labels) {
      const kleurcode = (lbl.kleurcode || "").trim();
      const barcode = (lbl.barcode || "").trim();
      let omschrijving = (lbl.omschrijving || "").trim();
      if (!kleurcode && !barcode && !omschrijving) omschrijving = "Onbekend etiket — controleren";
      try {
        const res = await axios.post(`${API}/stock-bijvullen`, {
          ...doelRef.current,
          kleurcode,
          barcode,
          omschrijving,
          merk: lbl.merk || "",
          maat: lbl.maat || "",
          prijs: lbl.prijs ? parseFloat(lbl.prijs) : null,
          aantal: Math.max(1, parseInt(lbl.aantal || 1, 10)),
          per_pack: Math.max(1, parseInt(lbl.per_pack || 1, 10)),
          extra: lbl.extra || {},
          foto_url: lbl.foto_url || null,
        });
        if (res.data?.id) ids.push(res.data.id);
        ok += 1;
      } catch {
        /* overslaan */
      }
    }
    if (ids.length) {
      setNieuweIds((prev) => new Set([...prev, ...ids]));
      playBeep({ frequency: 1200, duration: 90 });
      await fetchItems();
    }
    return ok;
  }, [fetchItems]);

  const scanEnkeleFoto = useCallback((item) => {
    const job = (async () => {
      try {
        const fd = new FormData();
        fd.append("images", item.file);
        const res = await axios.post(`${API}/stock-bijvullen/scan`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const labels = res.data?.labels || [];
        scanResultsRef.current.set(item.id, labels);
        if (directOpslaanRef.current && labels.length) {
          const opgeslagen = await bewaarLabels(dedupLabels(labels));
          setPendingPhotos((prev) =>
            prev.map((p) =>
              p.id === item.id ? { ...p, status: "opgeslagen", aantalLabels: opgeslagen } : p
            )
          );
          return;
        }
        setPendingPhotos((prev) =>
          prev.map((p) => (p.id === item.id ? { ...p, status: labels.length ? "done" : "leeg", aantalLabels: labels.length } : p))
        );
      } catch {
        scanResultsRef.current.set(item.id, []);
        setPendingPhotos((prev) => prev.map((p) => (p.id === item.id ? { ...p, status: "error" } : p)));
      }
    })();
    scanJobsRef.current.set(item.id, job);
    return job;
  }, [bewaarLabels]);

  const voegFotosToe = useCallback(
    (files) => {
      const nieuw = files.map((f) => ({
        id: ++photoIdRef.current,
        file: f,
        url: URL.createObjectURL(f),
        status: "scanning",
        aantalLabels: 0,
      }));
      setPendingPhotos((prev) => [...prev, ...nieuw]);
      nieuw.forEach(scanEnkeleFoto);
      return nieuw.length;
    },
    [scanEnkeleFoto]
  );

  const handleAddPhotos = async (files) => {
    if (!files || files.length === 0) return;
    const validFiles = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (validFiles.length === 0) {
      toast.error("Selecteer minstens 1 afbeelding");
      return;
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
    const kleiner = await Promise.all(validFiles.map((f) => verkleinFoto(f)));
    const n = voegFotosToe(kleiner);
    toast.success(`${n} foto('s) toegevoegd · AI leest ze nu`, { duration: 1500 });
  };

  const handleCameraCapture = useCallback(
    (file) => {
      voegFotosToe([file]);
    },
    [voegFotosToe]
  );

  const handleRemovePendingPhoto = (idx) => {
    setPendingPhotos((prev) => {
      const copy = [...prev];
      const [removed] = copy.splice(idx, 1);
      if (removed?.url) URL.revokeObjectURL(removed.url);
      if (removed?.id) {
        scanResultsRef.current.delete(removed.id);
        scanJobsRef.current.delete(removed.id);
      }
      return copy;
    });
  };

  const handleClearPendingPhotos = () => {
    pendingPhotos.forEach((p) => p.url && URL.revokeObjectURL(p.url));
    scanResultsRef.current.clear();
    scanJobsRef.current.clear();
    setPendingPhotos([]);
  };

  const handleScanBatch = async () => {
    if (pendingPhotos.length === 0) {
      toast.error("Voeg eerst 1 of meer foto's toe");
      return;
    }
    setScanLoading(true);
    try {
      const ids = pendingPhotos.map((p) => p.id);
      await Promise.allSettled(ids.map((id) => scanJobsRef.current.get(id)).filter(Boolean));
      const labels = ids.flatMap((id) => scanResultsRef.current.get(id) || []);
      if (labels.length === 0) {
        toast.error("Geen etiketten herkend op de foto's");
        return;
      }
      // Dedup identieke etiketten (zelfde kleurcode + barcode) → aantal ophogen
      const deduped = dedupLabels(labels);
      const totalDetected = labels.reduce((s, l) => s + Math.max(1, parseInt(l.aantal || 1, 10)), 0);
      const dupsMerged = totalDetected - deduped.length;
      toast.success(
        `${deduped.length} uniek etiket${deduped.length !== 1 ? "ten" : ""} · ${totalDetected} stuks${dupsMerged > 0 ? ` (${dupsMerged} identieke samengeteld)` : ""}`
      );
      setScanDialog({ open: true, labels: deduped });
      handleClearPendingPhotos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan mislukt");
    } finally {
      setScanLoading(false);
    }
  };

  const handleSaveScannedLabels = async () => {
    const selected = scanDialog.labels.filter((l) => l._selected);
    if (selected.length === 0) {
      toast.error("Selecteer minstens 1 etiket");
      return;
    }
    let ok = 0;
    let fail = 0;
    for (const lbl of selected) {
      try {
        await axios.post(`${API}/stock-bijvullen`, {
          ...doelPayload(),
          kleurcode: lbl.kleurcode || "",
          barcode: lbl.barcode || "",
          omschrijving: lbl.omschrijving || "",
          merk: lbl.merk || "",
          maat: lbl.maat || "",
          prijs: lbl.prijs ? parseFloat(lbl.prijs) : null,
          aantal: Math.max(1, parseInt(lbl.aantal || 1, 10)),
          per_pack: Math.max(1, parseInt(lbl.per_pack || 1, 10)),
          extra: lbl.extra || {},
          foto_url: lbl.foto_url || null,
        });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    if (ok) toast.success(`${ok} etiket(ten) toegevoegd`);
    if (fail) toast.error(`${fail} etiket(ten) mislukt`);
    setScanDialog({ open: false, labels: [] });
    fetchItems();
  };

  // ── Barcode Scan (quick +1, modal blijft open voor volgende) ────
  const handleBarcodeScanned = async (code) => {
    if (!code) return;
    try {
      const res = await axios.post(`${API}/stock-bijvullen`, {
        ...doelPayload(),
        barcode: code,
        aantal: 1,
      });
      const nieuwAantal = res.data?.aantal || 1;
      toast.success(`${code} · aantal: ${nieuwAantal}`, { duration: 1500 });
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toevoegen mislukt");
    }
  };

  // ── PDF Bijvullijst ───────────────────────────────────────
  const handlePdfBijvullijst = () => {
    const params = new URLSearchParams(filterParams());
    openDownload(`${API}/stock-bijvullen/pdf-bijvullijst?${params.toString()}`);
  };

  // ── Aantal +/- ────────────────────────────────────────────────
  const changeAantal = async (item, delta) => {
    const nieuw = Math.max(0, (item.aantal || 0) + delta);
    if (nieuw === 0) {
      if (!window.confirm("Aantal wordt 0. Item verwijderen?")) return;
      return handleDelete(item.id);
    }
    try {
      await axios.put(`${API}/stock-bijvullen/${item.id}`, { aantal: nieuw });
      fetchItems();
    } catch {
      toast.error("Fout bij wijzigen aantal");
    }
  };

  // ── Pack inline update ─────────────────────────────────────
  const updatePerPack = async (item, waarde) => {
    const nieuw = Math.max(1, parseInt(waarde || 1, 10));
    if (nieuw === (item.per_pack || 1)) return;
    try {
      await axios.put(`${API}/stock-bijvullen/${item.id}`, { per_pack: nieuw });
      fetchItems();
    } catch {
      toast.error("Fout bij wijzigen pack");
    }
  };

  // ── Save (manual) ─────────────────────────────────────────────
  const handleSaveItem = async () => {
    const { item, isNew } = editDialog;
    if (!item) return;
    const payload = {
      filiaal_id: item.filiaal_id || null,
      kleurcode: item.kleurcode || "",
      barcode: item.barcode || "",
      omschrijving: item.omschrijving || "",
      merk: item.merk || "",
      maat: item.maat || "",
      prijs: item.prijs === "" || item.prijs === null ? null : parseFloat(item.prijs),
      aantal: Math.max(1, parseInt(item.aantal || 1, 10)),
      per_pack: Math.max(1, parseInt(item.per_pack || 1, 10)),
      notitie: item.notitie || "",
    };
    if (!payload.kleurcode && !payload.barcode && !payload.omschrijving) {
      toast.error("Vul minstens kleurcode, barcode of omschrijving in");
      return;
    }
    try {
      if (isNew) {
        await axios.post(`${API}/stock-bijvullen`, payload);
        toast.success("Item toegevoegd");
      } else {
        await axios.put(`${API}/stock-bijvullen/${item.id}`, payload);
        toast.success("Item bijgewerkt");
      }
      setEditDialog({ open: false, item: null, isNew: true });
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fout bij opslaan");
    }
  };

  // ── Delete ────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Verwijder dit item?")) return;
    try {
      await axios.delete(`${API}/stock-bijvullen/${id}`);
      toast.success("Verwijderd");
      fetchItems();
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Verwijder ${selectedIds.size} item(s)?`)) return;
    try {
      await axios.post(`${API}/stock-bijvullen/batch-delete`, {
        ids: Array.from(selectedIds),
      });
      toast.success(`${selectedIds.size} verwijderd`);
      setSelectedIds(new Set());
      fetchItems();
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  // ── Export ────────────────────────────────────────────────────
  const handleExport = async () => {
    try {
      const params = new URLSearchParams(filterParams());
      openDownload(`${API}/stock-bijvullen/export?${params.toString()}`);
    } catch {
      toast.error("Export mislukt");
    }
  };

  // ── Datum-filter client-side ─────────────────────────────────
  const filteredItems = useMemo(() => {
    if (filterDatum === "all") return items;
    const now = new Date();
    let start = null;
    if (filterDatum === "today") {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else if (filterDatum === "week") {
      // Maandag van deze week
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dow = d.getDay() === 0 ? 6 : d.getDay() - 1; // 0=maandag
      d.setDate(d.getDate() - dow);
      start = d;
    } else if (filterDatum === "month") {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
    }
    if (!start) return items;
    return items.filter((it) => {
      if (!it.created_at) return false;
      const ts = new Date(it.created_at);
      return !isNaN(ts) && ts >= start;
    });
  }, [items, filterDatum]);

  // ── Sorteren op kolom ────────────────────────────────────────
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const sortOp = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const sortedItems = useMemo(() => {
    const waarde = (it) => {
      switch (sort.key) {
        case "barcode": return (it.barcode || "").toLowerCase();
        case "kleur": return kleurDeel(it.kleurcode, it.barcode).toLowerCase();
        case "filiaal": return (it.klant_id ? it.klant_naam : it.filiaal_naam || "").toLowerCase();
        case "omschrijving": return (it.omschrijving || "").toLowerCase();
        case "merk": return `${it.merk || ""} ${it.maat || ""}`.toLowerCase();
        case "prijs": return it.prijs || 0;
        case "aantal": return it.aantal || 0;
        case "pack": return Math.max(1, it.per_pack || 1);
        case "stuks": return (it.aantal || 0) * Math.max(1, it.per_pack || 1);
        default: return it.created_at || "";
      }
    };
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...filteredItems].sort((a, b) => {
      const va = waarde(a), vb = waarde(b);
      if (va === vb) return 0;
      return (typeof va === "number" ? va - vb : String(va).localeCompare(String(vb), "nl")) * factor;
    });
  }, [filteredItems, sort]);

  // ── AI-herlezen per rij ──────────────────────────────────────
  const handleRescan = async (item) => {
    if (!item.foto_url) {
      toast.error("Geen foto opgeslagen — kan niet opnieuw lezen");
      return;
    }
    try {
      toast.info(`AI leest ${item.kleurcode || "item"} opnieuw…`, { duration: 1500 });
      const res = await axios.post(`${API}/stock-bijvullen/${item.id}/rescan`);
      const d = res.data;
      toast.success(`Bijgewerkt: pack=${d.per_pack} · ${d.kleurcode || "—"}`);
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI-herlezen mislukt");
    }
  };

  // ── Totals ────────────────────────────────────────────────────
  const totals = useMemo(() => {
    let aantalTotaal = 0;
    let stuksTotaal = 0;
    let waarde = 0;
    for (const it of filteredItems) {
      const aantal = it.aantal || 0;
      const per_pack = Math.max(1, it.per_pack || 1);
      aantalTotaal += aantal;
      stuksTotaal += aantal * per_pack;
      if (it.prijs) waarde += (it.prijs || 0) * aantal * per_pack;
    }
    return { aantal: aantalTotaal, stuks: stuksTotaal, waarde, regels: filteredItems.length };
  }, [filteredItems]);

  const allSelected = filteredItems.length > 0 && filteredItems.every((it) => selectedIds.has(it.id));
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredItems.map((i) => i.id)));
  };

  return (
    <div className="space-y-5" data-testid="stock-bijvullen-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Package size={26} weight="bold" className="text-[#002FA7]" />
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              Stock Bijvullen
            </h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Maak foto&apos;s van etiketten → AI leest ze → dezelfde kleurcode wordt automatisch opgeteld.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchItems(); fetchKlanten(); }}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <ArrowsClockwise size={16} weight="bold" className={`mr-1 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Laden..." : "Verversen"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setBarcodeScannerOpen(true)}
            data-testid="scan-barcode-btn"
          >
            <Barcode size={16} weight="bold" className="mr-1" /> Scan barcode
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditDialog({ open: true, item: { ...emptyItem(), filiaal_id: defaultFiliaal, klant_id: defaultKlant || null }, isNew: true })}
            data-testid="add-manual-btn"
          >
            <Plus size={16} weight="bold" className="mr-1" /> Handmatig
          </Button>
          <Button variant="outline" size="sm" onClick={handlePdfBijvullijst} data-testid="pdf-bijvullijst-btn">
            <FilePdf size={16} weight="bold" className="mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-excel-btn">
            <FileXls size={16} weight="bold" className="mr-1" /> Excel
          </Button>
          {selectedIds.size > 0 && (
            <Button variant="destructive" size="sm" onClick={handleBatchDelete} data-testid="batch-delete-btn">
              <Trash size={16} weight="bold" className="mr-1" /> {selectedIds.size} verwijderen
            </Button>
          )}
        </div>
      </div>

      {/* Filters + Scan Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        {/* Filiaal */}
        <div className="bg-white border border-border rounded-sm p-4 space-y-2">
          <Label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Doel (voor nieuwe items)</Label>
          <Select value={doelWaarde} onValueChange={kiesDoel}>
            <SelectTrigger data-testid="default-filiaal-select">
              <SelectValue placeholder="Kies filiaal of klant" />
            </SelectTrigger>
            <SelectContent>
              {filialen.map((f) => (
                <SelectItem key={f.id} value={`fil:${f.id}`}>
                  {f.naam}
                </SelectItem>
              ))}
              {klanten.map((k) => (
                <SelectItem key={k.id} value={`kl:${k.id}`}>
                  Klant: {k.naam}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1.5 pt-1">
            <Input
              value={nieuweKlant}
              onChange={(e) => setNieuweKlant(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && voegKlantToe()}
              placeholder="Nieuwe klant (bv. Euro Mode)"
              className="h-9 text-xs"
              data-testid="nieuwe-klant-input"
            />
            <Button variant="outline" size="sm" className="h-9" onClick={voegKlantToe} data-testid="nieuwe-klant-btn">
              <Plus size={15} weight="bold" />
            </Button>
          </div>
          {klanten.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="klanten-lijst">
              {klanten.map((k) => (
                <span key={k.id} className="flex items-center bg-[#F3E8FF] border border-[#7C3AED]/30 rounded-sm">
                  <button
                    onClick={() => { setDefaultKlant(k.id); setDefaultFiliaal(""); }}
                    className="px-2 py-1 text-[11px] font-bold text-[#6B21A8]"
                    data-testid={`klant-kies-${k.id}`}
                  >
                    {k.naam}
                  </button>
                  <button
                    onClick={() => verwijderKlant(k)}
                    className="px-1.5 py-1 text-destructive border-l border-[#7C3AED]/20"
                    data-testid={`klant-del-${k.id}`}
                  >
                    <Trash size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <Label className="text-xs uppercase tracking-wider font-bold text-muted-foreground pt-2">Toon</Label>
          <div className="grid grid-cols-2 gap-2">
            <Select value={filterFiliaal} onValueChange={setFilterFiliaal}>
              <SelectTrigger data-testid="filter-filiaal-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alles</SelectItem>
                {filialen.map((f) => (
                  <SelectItem key={f.id} value={`fil:${f.id}`}>
                    {f.naam}
                  </SelectItem>
                ))}
                {klanten.map((k) => (
                  <SelectItem key={k.id} value={`kl:${k.id}`}>
                    Klant: {k.naam}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterDatum} onValueChange={setFilterDatum}>
              <SelectTrigger data-testid="filter-datum-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Vandaag</SelectItem>
                <SelectItem value="week">Deze week</SelectItem>
                <SelectItem value="month">Deze maand</SelectItem>
                <SelectItem value="all">Alle datums</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Scan */}
        <div className="md:col-span-2 bg-gradient-to-br from-[#002FA7] to-[#0044CC] text-white rounded-sm p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkle size={20} weight="fill" />
              <h2 className="text-lg font-bold">AI Etiket Scanner</h2>
            </div>
            <p className="text-sm text-white/80">
              Neem meerdere foto&apos;s achter elkaar → de AI leest elke foto direct, dus je hoeft nooit te wachten.
            </p>
          </div>

          {/* Pending photo strip */}
          {pendingPhotos.length > 0 && (
            <div className="mt-3 bg-white/10 rounded-sm p-2 border border-white/20" data-testid="pending-photos">
              <div className="text-[11px] uppercase tracking-wider font-bold text-white/70 mb-1">
                {scanStats.bezig > 0
                  ? `AI leest ${scanStats.bezig} foto${scanStats.bezig !== 1 ? "'s" : ""}... · ${scanStats.klaar} klaar · ${scanStats.labels} etiket(ten)`
                  : directOpslaan
                    ? `${pendingPhotos.length} foto's gelezen · ${scanStats.labels} etiket(ten) in de lijst gezet`
                    : `${pendingPhotos.length} foto's gelezen · ${scanStats.labels} etiket(ten) gevonden`}
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {pendingPhotos.map((p, i) => (
                  <div key={p.id} className="relative flex-shrink-0" data-testid={`pending-photo-${i}`}>
                    <img src={p.url} alt="" className="w-14 h-14 object-cover rounded-sm border border-white/40" />
                    {p.status === "scanning" && (
                      <div className="absolute inset-0 bg-black/55 flex items-center justify-center rounded-sm">
                        <ArrowClockwise size={18} weight="bold" className="animate-spin text-white" />
                      </div>
                    )}
                    {p.status === "opgeslagen" && (
                      <div className="absolute bottom-0 left-0 right-0 bg-emerald-600 text-white text-[10px] font-bold text-center rounded-b-sm flex items-center justify-center gap-0.5">
                        <Check size={9} weight="bold" /> {p.aantalLabels}
                      </div>
                    )}
                    {p.status === "done" && (
                      <div className="absolute bottom-0 left-0 right-0 bg-emerald-500 text-white text-[10px] font-bold text-center rounded-b-sm">
                        {p.aantalLabels}
                      </div>
                    )}
                    {p.status === "leeg" && (
                      <div className="absolute bottom-0 left-0 right-0 bg-amber-500 text-white text-[10px] font-bold text-center rounded-b-sm">
                        leeg
                      </div>
                    )}
                    {p.status === "error" && (
                      <div className="absolute bottom-0 left-0 right-0 bg-red-500 text-white text-[10px] font-bold text-center rounded-b-sm">
                        fout
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemovePendingPhoto(i)}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      data-testid={`remove-photo-${i}`}
                    >
                      <X size={10} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 mt-3 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => handleAddPhotos(e.target.files)}
              data-testid="scan-file-input"
            />
            <Button
              onClick={() => setCameraOpen(true)}
              disabled={scanLoading}
              className="bg-white text-[#002FA7] hover:bg-white/90 font-bold"
              data-testid="open-camera-btn"
            >
              <Camera size={18} weight="bold" className="mr-2" />
              {pendingPhotos.length === 0 ? "Camera openen" : "+ Nog scannen"}
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={scanLoading}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white/20"
              data-testid="add-photo-btn"
            >
              <Upload size={18} weight="bold" className="mr-2" />
              Galerij
            </Button>
            {pendingPhotos.length > 0 && !directOpslaan && (
              <>
                <Button
                  onClick={handleScanBatch}
                  disabled={scanLoading}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold"
                  data-testid="scan-batch-btn"
                >
                  {scanLoading ? (
                    <>
                      <ArrowClockwise size={18} weight="bold" className="mr-2 animate-spin" />
                      AI leest nog...
                    </>
                  ) : scanStats.bezig > 0 ? (
                    <>
                      <ArrowClockwise size={18} weight="bold" className="mr-2 animate-spin" />
                      Resultaten ({scanStats.klaar}/{pendingPhotos.length})
                    </>
                  ) : (
                    <>
                      <Sparkle size={18} weight="bold" className="mr-2" />
                      Resultaten · {scanStats.labels} etiket{scanStats.labels !== 1 ? "ten" : ""}
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClearPendingPhotos}
                  disabled={scanLoading}
                  className="bg-white/10 text-white border-white/40 hover:bg-white/20"
                  data-testid="clear-photos-btn"
                >
                  <Trash size={16} weight="bold" className="mr-1" />
                  Wissen
                </Button>
              </>
            )}
            {pendingPhotos.length > 0 && directOpslaan && (
              <Button
                variant="outline"
                onClick={handleClearPendingPhotos}
                className="bg-white/10 text-white border-white/40 hover:bg-white/20"
                data-testid="clear-photos-btn"
              >
                <Trash size={16} weight="bold" className="mr-1" />
                Foto's wissen
              </Button>
            )}
            <Button
              onClick={() => setBarcodeScannerOpen(true)}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white/20"
              data-testid="quick-barcode-btn"
            >
              <Barcode size={18} weight="bold" className="mr-2" />
              Snel barcode +1
            </Button>
          </div>

          <label className="flex items-center gap-2 mt-3 text-xs text-white/90 cursor-pointer w-fit">
            <Switch
              checked={directOpslaan}
              className="data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-white/30 border border-white/50"
              onCheckedChange={(v) => {
                setDirectOpslaan(v);
                localStorage.setItem("stock_direct_opslaan", v ? "1" : "0");
              }}
              data-testid="direct-opslaan-switch"
            />
            <span className="font-bold">Direct opslaan</span>
            <span className="text-white/60">
              {directOpslaan ? "etiketten gaan meteen in de lijst" : "eerst controleren in een venster"}
            </span>
          </label>
        </div>
      </div>

      {/* Totals bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-border rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Regels</div>
          <div className="text-2xl font-black" data-testid="total-regels">{totals.regels}</div>
        </div>
        <div className="bg-white border border-border rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Totaal aantal</div>
          <div className="text-2xl font-black" data-testid="total-aantal">{totals.aantal}</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#002FA7]">Totaal stuks (× pack)</div>
          <div className="text-2xl font-black text-[#002FA7]" data-testid="total-stuks">{totals.stuks}</div>
        </div>
        <div className="bg-white border border-border rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Totale waarde</div>
          <div className="text-2xl font-black" data-testid="total-waarde">€ {fmtEUR(totals.waarde)}</div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-sm text-muted-foreground py-10 text-center">Laden…</div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white border border-dashed border-border rounded-sm p-10 text-center">
          <Package size={40} weight="light" className="mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            {items.length === 0
              ? "Nog geen items. Scan een etiket of voeg handmatig toe."
              : `Geen items in geselecteerde periode. Kies "Alles" om alle items te tonen.`}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-sm overflow-x-auto" data-testid="stock-list">
          <p className="md:hidden text-[10px] text-muted-foreground px-3 pt-2 font-semibold">↔ Veeg zijwaarts voor alle kolommen</p>
          <table className="w-full text-sm min-w-[620px]">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider font-bold text-muted-foreground">
              <tr>
                <th className="p-2 w-8">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
                </th>
                <th className="p-2 text-left">Foto</th>
                <SortTh k="barcode" sort={sort} onClick={sortOp}>Barcode</SortTh>
                <SortTh k="kleur" sort={sort} onClick={sortOp}>Kleur</SortTh>
                <SortTh k="created_at" sort={sort} onClick={sortOp}>Datum</SortTh>
                <SortTh k="filiaal" sort={sort} onClick={sortOp}>Filiaal</SortTh>
                <SortTh k="omschrijving" sort={sort} onClick={sortOp}>Omschrijving</SortTh>
                <SortTh k="merk" sort={sort} onClick={sortOp}>Merk / Maat</SortTh>
                <SortTh k="prijs" sort={sort} onClick={sortOp} align="right">Prijs</SortTh>
                <SortTh k="aantal" sort={sort} onClick={sortOp} align="center">Aantal</SortTh>
                <SortTh k="pack" sort={sort} onClick={sortOp} align="center">Pack</SortTh>
                <SortTh k="stuks" sort={sort} onClick={sortOp} align="center">Totaal stuks</SortTh>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((it) => {
                const checked = selectedIds.has(it.id);
                return (
                  <SwipeableRow
                    key={it.id}
                    item={it}
                    isNieuw={nieuweIds.has(it.id)}
                    onDelete={(itm) => {
                      // Directe verwijdering na swipe (zonder confirm — de swipe zelf is de bevestiging)
                      axios
                        .delete(`${API}/stock-bijvullen/${itm.id}`)
                        .then(() => {
                          toast.success(`${itm.kleurcode || itm.barcode || "Item"} verwijderd`, { duration: 1500 });
                          fetchItems();
                        })
                        .catch(() => toast.error("Verwijderen mislukt"));
                    }}
                    data-testid={`stock-row-${it.id}`}
                  >
                    <td className="p-2 align-middle">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          const s = new Set(selectedIds);
                          if (v) s.add(it.id);
                          else s.delete(it.id);
                          setSelectedIds(s);
                        }}
                        data-testid={`select-row-${it.id}`}
                      />
                    </td>
                    <td className="p-2 align-middle">
                      {it.foto_url ? (
                        <img
                          src={`${BACKEND}${it.foto_url}`}
                          alt=""
                          className="w-12 h-12 object-cover rounded-sm border border-border cursor-pointer"
                          onClick={() => window.open(`${BACKEND}${it.foto_url}`, "_blank")}
                        />
                      ) : (
                        <div className="w-12 h-12 bg-muted rounded-sm flex items-center justify-center text-muted-foreground">
                          <Package size={20} weight="light" />
                        </div>
                      )}
                    </td>
                    <td className="p-2 align-middle text-xs font-mono">{it.barcode || "—"}</td>
                    <td className="p-2 align-middle">
                      <span className="font-bold" data-testid={`kleur-${it.id}`}>
                        {kleurDeel(it.kleurcode, it.barcode) || "—"}
                      </span>
                      {nieuweIds.has(it.id) && (
                        <Badge
                          className="ml-2 bg-emerald-500 text-white text-[10px] px-1.5 py-0"
                          data-testid={`nieuw-badge-${it.id}`}
                        >
                          NIEUW
                        </Badge>
                      )}
                    </td>
                    <td className="p-2 align-middle text-xs">
                      {fmtDateTime(it.created_at)}
                      {it.updated_at && it.updated_at !== it.created_at && (
                        <div className="text-[10px] text-muted-foreground">
                          laatst {fmtTijd(it.updated_at)}
                        </div>
                      )}
                    </td>
                    <td className="p-2 align-middle text-xs">
                      {it.klant_id ? `Klant: ${it.klant_naam || "—"}` : (it.filiaal_naam || "—")}
                    </td>
                    <td className="p-2 align-middle">{it.omschrijving || "—"}</td>
                    <td className="p-2 align-middle text-xs">
                      {it.merk && <div>{it.merk}</div>}
                      {it.maat && <div className="text-muted-foreground">Maat {it.maat}</div>}
                      {!it.merk && !it.maat && "—"}
                    </td>
                    <td className="p-2 align-middle text-right">
                      {it.prijs ? `€ ${fmtEUR(it.prijs)}` : "—"}
                    </td>
                    <td className="p-2 align-middle">
                      <div className="flex flex-col items-center gap-0.5">
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-7 w-7"
                            onClick={() => changeAantal(it, -1)}
                            data-testid={`minus-${it.id}`}
                          >
                            <Minus size={12} weight="bold" />
                          </Button>
                          <Badge variant="secondary" className="min-w-[32px] justify-center text-sm font-bold">
                            {it.aantal || 0}
                          </Badge>
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-7 w-7"
                            onClick={() => changeAantal(it, +1)}
                            data-testid={`plus-${it.id}`}
                          >
                            <Plus size={12} weight="bold" />
                          </Button>
                        </div>
                      </div>
                    </td>
                    <td className="p-2 align-middle text-center">
                      <Input
                        type="number"
                        min="1"
                        value={Math.max(1, it.per_pack || 1)}
                        onChange={(e) => {
                          // Optimistic local update via re-render trick: skip, use blur
                          const v = e.target.value;
                          const copy = items.map((x) => x.id === it.id ? { ...x, per_pack: parseInt(v || 1, 10) } : x);
                          setItems(copy);
                        }}
                        onBlur={(e) => updatePerPack(it, e.target.value)}
                        className="h-7 w-14 text-center font-mono px-1"
                        data-testid={`pack-input-${it.id}`}
                      />
                    </td>
                    <td
                      className="p-2 align-middle text-center font-mono font-bold"
                      data-testid={`stuks-${it.id}`}
                    >
                      {(it.aantal || 0) * Math.max(1, it.per_pack || 1)}
                    </td>
                    <td className="p-2 align-middle text-right">
                      <div className="flex justify-end gap-1">
                        {it.foto_url && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-[#002FA7]"
                            onClick={() => handleRescan(it)}
                            title="AI opnieuw laten lezen"
                            data-testid={`rescan-${it.id}`}
                          >
                            <Sparkle size={14} weight="bold" />
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => setEditDialog({ open: true, item: { ...it }, isNew: false })}
                          data-testid={`edit-${it.id}`}
                        >
                          <PencilSimple size={14} weight="bold" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive"
                          onClick={() => handleDelete(it.id)}
                          data-testid={`delete-${it.id}`}
                        >
                          <Trash size={14} weight="bold" />
                        </Button>
                      </div>
                    </td>
                  </SwipeableRow>
                );
              })}
            </tbody>
            <tfoot className="bg-[#002FA7] text-white text-sm font-bold" data-testid="totaal-rij">
              <tr>
                <td colSpan={8} className="p-2 text-right uppercase tracking-wider text-xs">
                  Totaal ({totals.regels} regel{totals.regels !== 1 ? "s" : ""})
                </td>
                <td className="p-2 text-right font-mono" data-testid="totaal-waarde">
                  {totals.waarde > 0 ? `€ ${totals.waarde.toFixed(2)}` : "—"}
                </td>
                <td className="p-2 text-center font-mono" data-testid="totaal-aantal">
                  {totals.aantal}
                </td>
                <td className="p-2"></td>
                <td className="p-2 text-center font-mono text-base" data-testid="totaal-stuks">
                  {totals.stuks}
                </td>
                <td className="p-2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Scan Preview Dialog */}
      <Dialog open={scanDialog.open} onOpenChange={(v) => !v && setScanDialog({ open: false, labels: [] })}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Herkende etiketten ({scanDialog.labels.length})</DialogTitle>
            <DialogDescription>
              Controleer en pas aan waar nodig. Zelfde kleurcode wordt automatisch opgeteld bij bestaande items.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-3 py-2">
            {scanDialog.labels.map((lbl, idx) => (
              <div
                key={idx}
                className={`border rounded-sm p-3 ${lbl._selected ? "bg-blue-50/60 border-blue-200" : "bg-muted/30 border-border opacity-60"}`}
                data-testid={`scan-label-${idx}`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={lbl._selected}
                    onCheckedChange={(v) => {
                      const copy = [...scanDialog.labels];
                      copy[idx] = { ...copy[idx], _selected: !!v };
                      setScanDialog({ ...scanDialog, labels: copy });
                    }}
                    className="mt-1"
                  />
                  {lbl.foto_url && (
                    <img
                      src={`${BACKEND}${lbl.foto_url}`}
                      alt=""
                      className="w-20 h-20 object-cover rounded-sm border border-border"
                    />
                  )}
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase text-muted-foreground">Kleurcode</Label>
                      <Input
                        value={lbl.kleurcode || ""}
                        onChange={(e) => {
                          const c = [...scanDialog.labels];
                          c[idx] = { ...c[idx], kleurcode: e.target.value };
                          setScanDialog({ ...scanDialog, labels: c });
                        }}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-muted-foreground">Barcode</Label>
                      <Input
                        value={lbl.barcode || ""}
                        onChange={(e) => {
                          const c = [...scanDialog.labels];
                          c[idx] = { ...c[idx], barcode: e.target.value };
                          setScanDialog({ ...scanDialog, labels: c });
                        }}
                        className="h-8 font-mono text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-muted-foreground">Prijs €</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={lbl.prijs ?? ""}
                        onChange={(e) => {
                          const c = [...scanDialog.labels];
                          c[idx] = { ...c[idx], prijs: e.target.value };
                          setScanDialog({ ...scanDialog, labels: c });
                        }}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-muted-foreground">Aantal</Label>
                      <Input
                        type="number"
                        min="1"
                        value={lbl.aantal || 1}
                        onChange={(e) => {
                          const c = [...scanDialog.labels];
                          c[idx] = { ...c[idx], aantal: e.target.value };
                          setScanDialog({ ...scanDialog, labels: c });
                        }}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-muted-foreground">Per pack</Label>
                      <Input
                        type="number"
                        min="1"
                        value={lbl.per_pack || 1}
                        onChange={(e) => {
                          const c = [...scanDialog.labels];
                          c[idx] = { ...c[idx], per_pack: e.target.value };
                          setScanDialog({ ...scanDialog, labels: c });
                        }}
                        className="h-8"
                        data-testid={`scan-per-pack-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] uppercase text-muted-foreground">Omschrijving</Label>
                      <Input
                        value={lbl.omschrijving || ""}
                        onChange={(e) => {
                          const c = [...scanDialog.labels];
                          c[idx] = { ...c[idx], omschrijving: e.target.value };
                          setScanDialog({ ...scanDialog, labels: c });
                        }}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-muted-foreground">Merk</Label>
                      <Input
                        value={lbl.merk || ""}
                        onChange={(e) => {
                          const c = [...scanDialog.labels];
                          c[idx] = { ...c[idx], merk: e.target.value };
                          setScanDialog({ ...scanDialog, labels: c });
                        }}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-muted-foreground">Maat</Label>
                      <Input
                        value={lbl.maat || ""}
                        onChange={(e) => {
                          const c = [...scanDialog.labels];
                          c[idx] = { ...c[idx], maat: e.target.value };
                          setScanDialog({ ...scanDialog, labels: c });
                        }}
                        className="h-8"
                      />
                    </div>
                    {lbl.extra && Object.keys(lbl.extra).length > 0 && (
                      <div className="col-span-full text-[11px] text-muted-foreground bg-muted/50 rounded-sm p-2 mt-1">
                        <span className="font-bold">Extra van etiket: </span>
                        {Object.entries(lbl.extra)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(" · ")}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScanDialog({ open: false, labels: [] })}>
              <X size={16} className="mr-1" /> Annuleren
            </Button>
            <Button onClick={handleSaveScannedLabels} data-testid="save-scanned-btn">
              <Check size={16} weight="bold" className="mr-1" />
              {scanDialog.labels.filter((l) => l._selected).length} toevoegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual add / edit dialog */}
      <Dialog
        open={editDialog.open}
        onOpenChange={(v) => !v && setEditDialog({ open: false, item: null, isNew: true })}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editDialog.isNew ? "Nieuw item" : "Item bewerken"}</DialogTitle>
            <DialogDescription>
              {editDialog.isNew
                ? "Bestaande kleurcode? De aantal wordt opgeteld bij dat item."
                : "Wijzig de gegevens van dit item."}
            </DialogDescription>
          </DialogHeader>
          {editDialog.item && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label>Filiaal</Label>
                <Select
                  value={editDialog.item.filiaal_id || ""}
                  onValueChange={(v) =>
                    setEditDialog({ ...editDialog, item: { ...editDialog.item, filiaal_id: v } })
                  }
                >
                  <SelectTrigger data-testid="edit-filiaal">
                    <SelectValue placeholder="Kies filiaal" />
                  </SelectTrigger>
                  <SelectContent>
                    {filialen.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.naam}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Kleurcode *</Label>
                <Input
                  value={editDialog.item.kleurcode || ""}
                  onChange={(e) =>
                    setEditDialog({ ...editDialog, item: { ...editDialog.item, kleurcode: e.target.value } })
                  }
                  placeholder="bv. ROOD-123"
                  data-testid="edit-kleurcode"
                />
              </div>
              <div>
                <Label>Barcode</Label>
                <Input
                  value={editDialog.item.barcode || ""}
                  onChange={(e) =>
                    setEditDialog({ ...editDialog, item: { ...editDialog.item, barcode: e.target.value } })
                  }
                  className="font-mono"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Omschrijving</Label>
                <Input
                  value={editDialog.item.omschrijving || ""}
                  onChange={(e) =>
                    setEditDialog({ ...editDialog, item: { ...editDialog.item, omschrijving: e.target.value } })
                  }
                />
              </div>
              <div>
                <Label>Merk</Label>
                <Input
                  value={editDialog.item.merk || ""}
                  onChange={(e) =>
                    setEditDialog({ ...editDialog, item: { ...editDialog.item, merk: e.target.value } })
                  }
                />
              </div>
              <div>
                <Label>Maat</Label>
                <Input
                  value={editDialog.item.maat || ""}
                  onChange={(e) =>
                    setEditDialog({ ...editDialog, item: { ...editDialog.item, maat: e.target.value } })
                  }
                />
              </div>
              <div>
                <Label>Prijs (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editDialog.item.prijs ?? ""}
                  onChange={(e) =>
                    setEditDialog({ ...editDialog, item: { ...editDialog.item, prijs: e.target.value } })
                  }
                />
              </div>
              <div>
                <Label>Aantal</Label>
                <Input
                  type="number"
                  min="1"
                  value={editDialog.item.aantal || 1}
                  onChange={(e) =>
                    setEditDialog({ ...editDialog, item: { ...editDialog.item, aantal: e.target.value } })
                  }
                />
              </div>
              <div>
                <Label>Per pack</Label>
                <Input
                  type="number"
                  min="1"
                  value={editDialog.item.per_pack || 1}
                  onChange={(e) =>
                    setEditDialog({ ...editDialog, item: { ...editDialog.item, per_pack: e.target.value } })
                  }
                  data-testid="edit-per-pack"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Notitie</Label>
                <Textarea
                  value={editDialog.item.notitie || ""}
                  onChange={(e) =>
                    setEditDialog({ ...editDialog, item: { ...editDialog.item, notitie: e.target.value } })
                  }
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, item: null, isNew: true })}>
              Annuleren
            </Button>
            <Button onClick={handleSaveItem} data-testid="save-item-btn">
              <Check size={16} weight="bold" className="mr-1" /> Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Live foto camera */}
      <FotoCameraModal
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCameraCapture}
        aantal={pendingPhotos.length}
        bezig={scanStats.bezig}
        labels={scanStats.labels}
      />

      {/* Barcode scanner */}
      <BarcodeScannerModal
        open={barcodeScannerOpen}
        onClose={() => setBarcodeScannerOpen(false)}
        onScan={handleBarcodeScanned}
      />
    </div>
  );
}